/**
 * SYSTELOS MONITOR - Escritório Service
 * Chamadas à API de configurações do escritório
 *
 * @version 1.0.0
 * @date 26/01/2026
 */

import api from './api'

// ============================================================================
// TIPOS
// ============================================================================

export interface ConfiguracoesEscritorio {
  email_notificacao: string
  emails_extras: string[]
  whatsapp_notificacao: string
  horario_envio: string
  notificar_novas_comunicacoes: boolean
  notificar_vencimentos: boolean
  notificar_resumo_diario: boolean
  nome: string
  plano: string
}

export interface AtualizarConfiguracoesDTO {
  email_notificacao: string
  emails_extras?: string[]
  whatsapp_notificacao?: string | null
  horario_envio?: string
  notificar_novas_comunicacoes?: boolean
  notificar_vencimentos?: boolean
  notificar_resumo_diario?: boolean
}

export interface DadosEscritorio {
  id: string
  nome: string
  razao_social: string
  cnpj: string
  email: string
  telefone: string
  plano: string
  ativo: boolean
  emails_notificacao: string[]
  whatsapp_principal: string | null
  _count: {
    clientes: number
    usuarios: number
  }
}

export interface Usuario {
  id: string
  nome: string
  email: string
  nivel: 'ADMIN' | 'OPERADOR' | 'VISUALIZADOR'
  ativo: boolean
  ultimo_acesso: string | null
  created_at: string
}

// ============================================================================
// SERVICE
// ============================================================================

const escritorioService = {
  /**
   * Buscar dados do escritório
   */
  async buscarDados(): Promise<DadosEscritorio> {
    const response = await api.get('/escritorio')
    return response.data.data
  },

  /**
   * Buscar configurações de notificação
   */
  async buscarConfiguracoes(): Promise<ConfiguracoesEscritorio> {
    const response = await api.get('/escritorio/configuracoes')
    return response.data.data
  },

  /**
   * Atualizar configurações de notificação
   */
  async atualizarConfiguracoes(dados: AtualizarConfiguracoesDTO): Promise<ConfiguracoesEscritorio> {
    const response = await api.put('/escritorio/configuracoes', dados)
    return response.data.data
  },

  /**
   * Listar usuários do escritório
   */
  async listarUsuarios(): Promise<Usuario[]> {
    const response = await api.get('/escritorio/usuarios')
    return response.data.data
  },

  /**
   * Criar novo usuário
   */
  async criarUsuario(dados: {
    nome: string
    email: string
    senha: string
    nivel: 'ADMIN' | 'OPERADOR' | 'VISUALIZADOR'
  }): Promise<Usuario> {
    const response = await api.post('/escritorio/usuarios', dados)
    return response.data.data
  },

  /**
   * Atualizar usuário
   */
  async atualizarUsuario(
    id: string,
    dados: Partial<{
      nome: string
      email: string
      nivel: 'ADMIN' | 'OPERADOR' | 'VISUALIZADOR'
      ativo: boolean
    }>
  ): Promise<Usuario> {
    const response = await api.put(`/escritorio/usuarios/${id}`, dados)
    return response.data.data
  },

  /**
   * Deletar usuário
   */
  async deletarUsuario(id: string): Promise<void> {
    await api.delete(`/escritorio/usuarios/${id}`)
  },

  /**
   * Enviar email de teste
   */
  async enviarEmailTeste(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/escritorio/email-teste', { email })
    return response.data
  },
}

export default escritorioService

